import { ROUTES } from "../../constants/routes";
import { Component, inject } from "@angular/core";
import { RouterLink } from "@angular/router";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { Navbar } from "../../components/navbar/navbar";
import { Breadcrumb } from "../../components/breadcrumb/breadcrumb";
import { Footer } from "../../components/footer/footer";
import { SeoService } from "../../services/seo.service";
import { ICONS } from "../../constants/icons";
import { CONFIRMATION_STEPS, DATA_SOURCES, SOURCING_FAQS } from "../../constants/data-sources";
import {
  breadcrumbSchema,
  faqSchema,
  howToSchema,
  webPageSchema
} from "../../helpers/structured-data";

@Component({
  selector: "app-how-it-works",
  imports: [RouterLink, Breadcrumb, Navbar, Footer],
  templateUrl: "./how-it-works.html"
})
export class HowItWorks {
  readonly routes = ROUTES;
  private readonly seo = inject(SeoService);
  private readonly sanitizer = inject(DomSanitizer);

  readonly dataSources = DATA_SOURCES;
  readonly confirmationSteps = CONFIRMATION_STEPS;

  iconFor(name: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(ICONS[name] ?? "");
  }

  steps = [
    {
      title: "We collect and confirm deceased records",
      description:
        "Our team gathers deceased consumer records from authoritative sources nationwide and confirms each record before it enters the database. New records are added every week, often within seven days of a person passing away."
    },
    {
      title: "You prepare and upload your list",
      description:
        "Export the records you want to check into a CSV or Excel file. Include first name and last name at a minimum. Adding middle name, date of birth, city or state improves match accuracy. There is no limit on file size."
    },
    {
      title: "Our engine matches your records",
      description:
        "VerifyDeceased compares each of your records against millions of confirmed deceased records. Advanced name matching handles nicknames, middle initials and common spelling variations to reduce false positives and false negatives."
    },
    {
      title: "You review confirmed matches",
      description:
        "Every confirmed match returns date of birth, date of death, age at death and last known property address with a confidence score. Download the results as a file or receive them through the API."
    },
    {
      title: "You act with confidence",
      description:
        "Suppress accounts, decline applications, pause outreach or flag claims for review. You pay only for the records we confirm as deceased, so there is no cost for records we cannot verify."
    }
  ];

  inputs = [
    { field: "First name", required: "Required" },
    { field: "Last name", required: "Required" },
    { field: "Middle name", required: "Recommended" },
    { field: "Date of birth", required: "Recommended" },
    { field: "City", required: "Optional" },
    { field: "State", required: "Optional" }
  ];

  outputs = [
    { field: "Deceased status", note: "Confirmed match or no match" },
    { field: "Date of birth", note: "On file for the deceased consumer" },
    { field: "Date of death", note: "Confirmed date of passing" },
    { field: "Age at death", note: "Calculated for you" },
    { field: "Property address", note: "Last known property and mailing address" },
    { field: "Confidence score", note: "How strong the match is" }
  ];

  faqs = [
    ...SOURCING_FAQS,
    {
      question: "How long does verification take?",
      answer:
        "Batch file matching typically completes in minutes. Real time verification through the API returns a result in milliseconds, so you can screen an identity during account opening or a credit application without slowing the customer down."
    },
    {
      question: "What file formats can I upload?",
      answer:
        "You can upload CSV or Excel files. Each row should represent one person, with columns for first name, last name and any optional fields such as middle name, date of birth, city and state."
    },
    {
      question: "How accurate is the matching?",
      answer:
        "Our matching engine uses name normalization and additional identifiers such as date of birth and location to return high confidence matches. Every match includes a confidence score so your team can set the threshold that fits your risk tolerance."
    }
  ];

  ngOnInit(): void {
    this.seo.apply({
      title: "How Death Verification Works",
      description:
        "Upload a CSV or Excel list, match it against fresh nationwide death data sourced from vital records, the SSA Death Master File, obituaries and probate filings, and get results in minutes.",
      path: "/how-it-works",
      keywords:
        "how death verification works, deceased matching process, verify deceased consumer, death record matching, batch death verification, death data sources, SSA death master file, vital records death data",
      structuredData: [
        breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "How it works", path: "/how-it-works" }
        ]),
        webPageSchema({
          name: "How death verification works",
          description:
            "The five step process VerifyDeceased uses to confirm whether a consumer is deceased.",
          path: "/how-it-works"
        }),
        howToSchema(this.steps.map(step => ({ name: step.title, text: step.description }))),
        faqSchema(this.faqs)
      ]
    });
  }
}
